import type {
    BoardLock,
    BoardMove,
    BoardMoveBatch,
    BoardMoveRefusal,
    BoardPlacement,
} from "./Board.types";
import {
    getCellKey,
    getPlacementKey,
    type BoardCellEntry,
    type BoardCells,
} from "./boardCells";
import {
    getCopiesByItem,
    getMoveEnds,
    getMoveRefusal,
    type BoardDragGroup,
    type BoardMoveEnds,
    type BoardMoveRule,
} from "./boardDragGroup";
import {
    getEntriesWithout,
    getItemAfterMove,
    getSortedIndex,
    type BoardDropTarget,
} from "./boardMove";
import type { BoardMovedItemGetter } from "./boardSort.types";

const LOCKED_REASON = "Locked cards can't be moved";

interface ResolveBoardGroupDropArgs<TItem> {
    cells: BoardCells<TItem>;
    cellOrder: readonly BoardPlacement[];
    group: BoardDragGroup;
    /** The grabbed card's placement key: the drop's slot is measured without it. */
    activeKey: string;
    target: BoardDropTarget;
    /** Sorted mode, unless the cards are placed by hand. */
    compareItems?: (first: TItem, second: TItem) => number;
    getMovedItem?: BoardMovedItemGetter<TItem>;
    /** The cards are placed by hand in a sorted column. */
    isPlacedInSort: boolean;
    canMove?: BoardMoveRule<TItem>;
    getLock?: (item: TItem) => BoardLock | null;
}

export interface BoardGroupDropResolution<TItem> {
    /** Cells with every move applied, for rendering until the surface's update lands. */
    cells: BoardCells<TItem>;
    batch: BoardMoveBatch<TItem>;
    /** The grabbed card's placement key once the batch is applied. */
    focusKey: string;
    /** The other moved cards' placement keys once applied, in board order. */
    landingKeys: readonly string[];
}

/**
 * Splits the group into moves and refusals. Locked cards stay put as
 * refusals. A card whose cell doesn't change, or an item that already has
 * a copy in its target cell, stays put, except in the hovered cell when
 * cards land at the slot, where it joins the run.
 */
export const sortOutGroup = <TItem>({
    cells,
    cellOrder,
    group,
    activeKey,
    target,
    compareItems,
    canMove,
    getLock,
}: Pick<
    ResolveBoardGroupDropArgs<TItem>,
    | "cells"
    | "cellOrder"
    | "group"
    | "activeKey"
    | "target"
    | "compareItems"
    | "canMove"
    | "getLock"
>) => {
    const hoveredKey = getCellKey(target.placement);
    const landings: Array<BoardMoveEnds<TItem>> = [];
    const refusals: Array<BoardMoveRefusal<TItem>> = [];
    const itemIds = new Set([...group.itemIds, ...group.lockedItemIds]);
    getCopiesByItem(cells, cellOrder, itemIds).forEach((copies, itemId) => {
        const ends = getMoveEnds({
            copies,
            target: target.placement,
            isLaneFixed: group.isLaneFixed,
            activeKey,
        });
        if (group.lockedItemIds.has(itemId)) {
            refusals.push({
                item: ends.from.item,
                reason: getLock?.(ends.from.item)?.reason ?? LOCKED_REASON,
            });
            return;
        }
        const toKey = getCellKey(ends.to);
        if (getCellKey(ends.from.placement) === toKey) {
            if (compareItems === undefined && toKey === hoveredKey) {
                landings.push(ends);
            }
            return;
        }
        const reason = getMoveRefusal(ends, canMove);
        if (reason === undefined) {
            landings.push(ends);
        } else {
            refusals.push({ item: ends.from.item, reason });
        }
    });
    return { landings, refusals };
};

/** Every move stays in the hovered cell, which ends in the same order. */
const isReorderUndone = <TItem>(
    moves: ReadonlyArray<BoardMove<TItem>>,
    before: ReadonlyArray<BoardCellEntry<TItem>>,
    after: ReadonlyArray<BoardCellEntry<TItem>>,
) =>
    moves.every(({ from, to }) => getCellKey(from) === getCellKey(to)) &&
    before.every((entry, index) => entry.itemId === after[index]?.itemId);

/**
 * One group drop: every card moves to the target column, and lane unless
 * lanes are fixed. In sorted mode each card lands where the comparator puts
 * it as the move leaves it. In manual mode, or placed by hand, the cards land together at the
 * slot in board order: the first between the slot's visible neighbours,
 * each next one right after the one before. Cards landing in another cell
 * (a fixed lane) gather at its end the same way. Moves are reported in that
 * order with the neighbours they have once the moves before them are
 * applied.
 */
export const resolveBoardGroupDrop = <TItem>(
    args: ResolveBoardGroupDropArgs<TItem>,
): BoardGroupDropResolution<TItem> => {
    const {
        cells,
        activeKey,
        target,
        compareItems,
        getMovedItem,
        isPlacedInSort,
    } = args;
    const { landings, refusals } = sortOutGroup(args);
    const liftedKeys = new Set(landings.map(({ from }) => from.placementKey));
    const entriesByCell = new Map(cells.entriesByCell);
    landings.forEach(({ from }) => {
        const cellKey = getCellKey(from.placement);
        entriesByCell.set(
            cellKey,
            (entriesByCell.get(cellKey) ?? []).filter(
                (entry) => !liftedKeys.has(entry.placementKey),
            ),
        );
    });
    const hoveredKey = getCellKey(target.placement);
    const slotIndex = getEntriesWithout(cells, target.placement, activeKey)
        .slice(0, target.index)
        .filter((entry) => !liftedKeys.has(entry.placementKey)).length;
    const nextIndexByCell = new Map([[hoveredKey, slotIndex]]);

    let focusKey = activeKey;
    const landingKeys: string[] = [];
    const moves = landings.map(({ from, to }): BoardMove<TItem> => {
        const toKey = getCellKey(to);
        const entries = entriesByCell.get(toKey) ?? [];
        const index =
            compareItems === undefined
                ? (nextIndexByCell.get(toKey) ?? entries.length)
                : getSortedIndex(
                      entries,
                      getItemAfterMove(
                          { item: from.item, from: from.placement, to },
                          getMovedItem,
                      ),
                      compareItems,
                  );
        nextIndexByCell.set(toKey, index + 1);
        const placementKey = getPlacementKey(from.itemId, to);
        if (from.placementKey === activeKey) {
            focusKey = placementKey;
        } else {
            landingKeys.push(placementKey);
        }
        const landed: BoardCellEntry<TItem> = {
            ...from,
            placement: to,
            // The grabbed card keeps the drag's identity until the drop settles.
            placementKey:
                from.placementKey === activeKey ? activeKey : placementKey,
        };
        entriesByCell.set(toKey, [
            ...entries.slice(0, index),
            landed,
            ...entries.slice(index),
        ]);
        return {
            item: from.item,
            from: from.placement,
            to,
            beforeItemId: entries[index - 1]?.itemId ?? null,
            afterItemId: entries[index]?.itemId ?? null,
            isPlacedInSort,
        };
    });

    if (
        isReorderUndone(
            moves,
            cells.entriesByCell.get(hoveredKey) ?? [],
            entriesByCell.get(hoveredKey) ?? [],
        )
    ) {
        return {
            cells,
            batch: { moves: [], refusals },
            focusKey: activeKey,
            landingKeys: [],
        };
    }
    return {
        cells: { ...cells, entriesByCell },
        batch: { moves, refusals },
        focusKey,
        landingKeys,
    };
};
